import {
  ORGX_HOSTED_OAUTH_AUTHORIZE_URL,
  ORGX_HOSTED_OAUTH_REGISTER_URL,
  ORGX_HOSTED_OAUTH_TOKEN_URL,
  ORGX_WIZARD_OAUTH_CALLBACK_PATH,
  ORGX_WIZARD_OAUTH_PREFERRED_PORT,
  ORGX_WIZARD_OAUTH_SCOPE,
} from "../constants.js";
import { isRecord } from "./fs.js";
import { buildRedirectUri, startLocalAuthServer } from "./local-auth-server.js";
import { buildAuthorizeUrl, generatePkce, generateState } from "./pkce.js";

export interface OAuthTokenResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_token?: string;
  scope?: string;
}

export interface RegisteredOAuthClient {
  client_id: string;
  client_secret?: string;
}

export interface PkceLoginResult {
  accessToken: string;
  refreshToken: string | undefined;
  redirectUri: string;
  clientId: string;
  expiresIn: number | undefined;
}

async function registerPublicClient(
  options: {
    redirectUri: string;
    fetchImpl?: typeof fetch;
  },
): Promise<RegisteredOAuthClient> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const response = await fetchImpl(ORGX_HOSTED_OAUTH_REGISTER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_name: "OrgX Wizard CLI",
      redirect_uris: [options.redirectUri],
      token_endpoint_auth_method: "none",
      grant_types: ["authorization_code", "refresh_token"],
      response_types: ["code"],
    }),
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`OAuth client registration failed (HTTP ${response.status}): ${text}`);
  }

  const data = await response.json();
  if (!isRecord(data) || typeof data.client_id !== "string") {
    throw new Error("OAuth registration response missing client_id.");
  }

  return { client_id: data.client_id as string };
}

async function exchangeCodeForTokens(
  options: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
    clientId: string;
    fetchImpl?: typeof fetch;
  },
): Promise<OAuthTokenResponse> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code: options.code,
    redirect_uri: options.redirectUri,
    client_id: options.clientId,
    code_verifier: options.codeVerifier,
  });

  const response = await fetchImpl(ORGX_HOSTED_OAUTH_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Token exchange failed (HTTP ${response.status}): ${text}`);
  }

  const data = await response.json();
  if (!isRecord(data) || typeof data.access_token !== "string") {
    throw new Error("Token response missing access_token.");
  }

  return {
    access_token: data.access_token as string,
    token_type: typeof data.token_type === "string" ? data.token_type : "Bearer",
    ...(typeof data.expires_in === "number" ? { expires_in: data.expires_in } : {}),
    ...(typeof data.refresh_token === "string" ? { refresh_token: data.refresh_token } : {}),
    ...(typeof data.scope === "string" ? { scope: data.scope } : {}),
  };
}

export async function refreshAccessToken(
  options: {
    refreshToken: string;
    clientId: string;
    fetchImpl?: typeof fetch;
  },
): Promise<OAuthTokenResponse> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: options.refreshToken,
    client_id: options.clientId,
  });

  const response = await fetchImpl(ORGX_HOSTED_OAUTH_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Token refresh failed (HTTP ${response.status}): ${text}`);
  }

  const data = await response.json();
  if (!isRecord(data) || typeof data.access_token !== "string") {
    throw new Error("Refresh response missing access_token.");
  }

  return {
    access_token: data.access_token as string,
    token_type: typeof data.token_type === "string" ? data.token_type : "Bearer",
    ...(typeof data.expires_in === "number" ? { expires_in: data.expires_in } : {}),
    ...(typeof data.refresh_token === "string" ? { refresh_token: data.refresh_token } : {}),
  };
}

export interface StartPkceLoginOptions {
  /** Called with the URL the user should open. Return false to skip auto-open. */
  onAuthorizeUrl?: (url: string) => void;
  /** Override preferred callback port (default from constants). */
  preferredPort?: number;
  /** Override OAuth scopes. */
  scope?: string;
  /** Timeout in ms (default 5 min). */
  timeoutMs?: number;
  /** Injected fetch for testing. */
  fetchImpl?: typeof fetch;
}

/**
 * Full PKCE authorization_code flow:
 * 1. Register a public dynamic client
 * 2. Start local callback server
 * 3. Build authorize URL and open browser
 * 4. Wait for callback, validate state
 * 5. Exchange code for tokens
 *
 * Returns access_token + optional refresh_token — no OpenClaw required.
 */
export async function startPkceLogin(
  options: StartPkceLoginOptions = {},
): Promise<PkceLoginResult> {
  const port = options.preferredPort ?? ORGX_WIZARD_OAUTH_PREFERRED_PORT;
  const scope = options.scope ?? ORGX_WIZARD_OAUTH_SCOPE;
  const { codeVerifier, codeChallenge, codeChallengeMethod } = generatePkce();
  const state = generateState();

  // Start local server first so we know the actual bound port
  const serverOpts = {
    preferredPort: port,
    expectedState: state,
    callbackPath: ORGX_WIZARD_OAUTH_CALLBACK_PATH,
    ...(options.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
  };
  const { port: boundPort, result: callbackPromise } = await startLocalAuthServer(serverOpts);

  const redirectUri = buildRedirectUri(boundPort, ORGX_WIZARD_OAUTH_CALLBACK_PATH);

  const clientOpts = {
    redirectUri,
    ...(options.fetchImpl !== undefined ? { fetchImpl: options.fetchImpl } : {}),
  };
  const client = await registerPublicClient(clientOpts);

  const authorizeUrl = buildAuthorizeUrl({
    authorizeEndpoint: ORGX_HOSTED_OAUTH_AUTHORIZE_URL,
    clientId: client.client_id,
    redirectUri,
    scope,
    codeChallenge,
    codeChallengeMethod,
    state,
  });

  options.onAuthorizeUrl?.(authorizeUrl);

  const { code } = await callbackPromise;

  const exchangeOpts = {
    code,
    codeVerifier,
    redirectUri,
    clientId: client.client_id,
    ...(options.fetchImpl !== undefined ? { fetchImpl: options.fetchImpl } : {}),
  };
  const tokens = await exchangeCodeForTokens(exchangeOpts);

  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    redirectUri,
    clientId: client.client_id,
    expiresIn: tokens.expires_in,
  };
}
